import { isHtmlDateValue } from "@shared/ui/input";
import type { FieldSchema } from "@src/components/form";
import type { FormFieldDeclaration, ResolvedFieldValidationRules } from "./types";

/**
 * Ошибка поля в формате, совместимом с `react-hook-form` resolver.
 */
export type DeclarativeFieldError = {
    /** Тип ошибки (`required`, `min`, `validate`, ...). */
    type: string;
    /** Текст ошибки для отображения в UI. */
    message: string;
};

/**
 * Извлекает value из короткой/расширенной формы правила (`value` или `{ value, message }`).
 * @template TValue тип значения правила
 * @param rule описание правила
 * @returns нормализованное значение правила
 */
function getRuleValue<TValue>(rule: TValue | { value: TValue; message?: string }): TValue {
    if (typeof rule === "object" && rule != null && "value" in rule) {
        return (rule as { value: TValue }).value;
    }
    return rule;
}

/**
 * Возвращает message правила или дефолтный текст.
 * @param rule описание правила
 * @param fallback дефолтный текст, если message не задан
 */
function getRuleMessage(rule: boolean | number | RegExp | { value: unknown; message?: string }, fallback: string): string {
    if (typeof rule === "object" && rule != null && "message" in rule && typeof rule.message === "string") {
        return rule.message;
    }
    return fallback;
}

/**
 * Проверяет условие «поле заполнено» с учётом `FieldSchema.type`.
 * @param type тип поля
 * @param value текущее значение поля
 */
function isFilledByType(type: FieldSchema["type"], value: unknown): boolean {
    if (type === "string" || type === "nullable") {
        return value != null && value !== "";
    }
    if (type === "date") {
        if (typeof value === "string") {
            return isHtmlDateValue(value);
        }
        return false;
    }
    switch (type) {
        case "number":
            return value != null && typeof value === "number" && !Number.isNaN(value);
        case "boolean":
            return value === true;
        case "array":
            return Array.isArray(value) && value.length > 0;
        case "object":
            return value != null;
        default:
            return value != null;
    }
}

/**
 * Проверяет значение поля по уже вычисленным правилам валидации.
 * @template TForm тип значений формы
 * @template TDomain тип доменной модели
 * @template TFieldKey имя поля в форме
 * @param declaration декларация поля
 * @param resolvedRules вычисленные правила для текущего состояния формы
 * @param value текущее значение поля
 * @param formValues текущее состояние формы
 * @returns первую ошибку поля или `null`, если поле валидно
 */
export async function evaluateFieldRules<
    TForm extends object,
    TDomain extends object,
    TFieldKey extends keyof TForm,
    TContext = void,
>(
    declaration: FormFieldDeclaration<TForm, TDomain, TFieldKey, TContext>,
    resolvedRules: ResolvedFieldValidationRules<TContext> | undefined,
    value: unknown,
    formValues: TForm,
    context: TContext,
    options?: {
        /**
         * При `true` отключает short-circuit для disabled полей:
         * поле остаётся скрытым/невидимым, но его disabled-состояние не выключает валидацию.
         */
        validateDisabledFields?: boolean;
    },
): Promise<DeclarativeFieldError | null> {
    if (declaration.visible?.(formValues, context) === false) {
        return null;
    }
    const isDisabled = declaration.disabled?.(formValues, context) === true;
    if (isDisabled && options?.validateDisabledFields !== true) {
        return null;
    }
    if (resolvedRules == null) return null;
    if (resolvedRules.required != null && resolvedRules.required !== false) {
        if (!isFilledByType(declaration.type, value)) {
            return {
                type: "required",
                message: getRuleMessage(resolvedRules.required, "Обязательное поле"),
            };
        }
    }
    if (resolvedRules.minLength != null && resolvedRules.minLength !== false) {
        const minLength = getRuleValue(resolvedRules.minLength);
        if (typeof value === "string" && value.length < minLength) {
            return { type: "minLength", message: getRuleMessage(resolvedRules.minLength, `Минимум ${minLength} символов`) };
        }
        if (Array.isArray(value) && value.length < minLength) {
            return { type: "minLength", message: getRuleMessage(resolvedRules.minLength, `Минимум ${minLength} элементов`) };
        }
    }
    if (resolvedRules.maxLength != null && resolvedRules.maxLength !== false) {
        const maxLength = getRuleValue(resolvedRules.maxLength);
        if (typeof value === "string" && value.length > maxLength) {
            return { type: "maxLength", message: getRuleMessage(resolvedRules.maxLength, `Максимум ${maxLength} символов`) };
        }
        if (Array.isArray(value) && value.length > maxLength) {
            return {
                type: "maxLength",
                message: getRuleMessage(resolvedRules.maxLength, `Максимум ${maxLength} элементов`),
            };
        }
    }
    if (resolvedRules.min != null && resolvedRules.min !== false) {
        const min = getRuleValue(resolvedRules.min);
        if (typeof value === "number" && !Number.isNaN(value) && value < min) {
            return { type: "min", message: getRuleMessage(resolvedRules.min, `Минимум ${min}`) };
        }
    }
    if (resolvedRules.max != null && resolvedRules.max !== false) {
        const max = getRuleValue(resolvedRules.max);
        if (typeof value === "number" && !Number.isNaN(value) && value > max) {
            return { type: "max", message: getRuleMessage(resolvedRules.max, `Максимум ${max}`) };
        }
    }
    if (resolvedRules.integer != null && resolvedRules.integer !== false) {
        if (typeof value === "number" && !Number.isNaN(value) && !Number.isInteger(value)) {
            return {
                type: "integer",
                message: getRuleMessage(resolvedRules.integer, "Допустимо только целое число"),
            };
        }
    }
    if (declaration.type === "number" && resolvedRules.positive != null && resolvedRules.positive !== false) {
        if (typeof value === "number" && !Number.isNaN(value) && value <= 0) {
            const rule = resolvedRules.positive;
            const message =
                typeof rule === "object" && rule != null && typeof rule.message === "string"
                    ? rule.message
                    : "Должно быть больше 0";
            return { type: "positive", message };
        }
    }
    if (resolvedRules.pattern != null && resolvedRules.pattern !== false) {
        const pattern = getRuleValue(resolvedRules.pattern);
        if (typeof value === "string" && value !== "" && !pattern.test(value)) {
            return { type: "pattern", message: getRuleMessage(resolvedRules.pattern, "Неверный формат") };
        }
    }
    if (typeof resolvedRules.validate === "function") {
        const message = await Promise.resolve(resolvedRules.validate(value, formValues as Record<string, unknown>, context));
        if (typeof message === "string" && message.length > 0) {
            return { type: "validate", message };
        }
    }
    return null;
}
